/**
 * Middleware that sets some sane default for redux-segment tracked events and
 * augments analytics metadata to make your life easier, including:
 * - Setting analytics event type to a track event by default
 * - Automatically mapping the action name to a corresponding human-friendly
 *   tracking event you defined in the tracking.js file in the dux.
 * - Augmenting the properties on the event to include (mostly static) props
 *   that should be included with every tracking request, including things like
 *   userId, source, etc.
 */
import { EventTypes } from 'redux-segment';
import eventMap from './eventMap';
import { selectors } from '../../dux/user';

// Event auto-tracked by Redux Segment that need to be augmented
const AUTOTRACKED_EVENTS = [
  '@@router/INIT_PATH',
  '@@router/UPDATE_PATH',
  '@@router/LOCATION_CHANGE',
  '@@reduxReactRouter/initRoutes',
  '@@reduxReactRouter/routerDidChange',
  '@@reduxReactRouter/replaceRoutes',
];

/**
 * Converts the action from a shorthand format that only specifies the event
 * type to a full action that includes an eventType and an eventPayload object
 * with a properties object.
 *
 * @param {object} action - Action to expand with metadata
 * @param {object} defaultEventType - The event type to use if not already
 * specififed in the action metadata
 * @returns {object} - The action augmented with analytics metadata
 */
const expandAnalyticsMetadata = (action, defaultEventType = EventTypes.track) => {
  const newAction = JSON.parse(JSON.stringify(action));
  let trackMeta;

  newAction.meta = newAction.meta || {};
  newAction.meta.analytics = newAction.meta.analytics || {};
  switch (typeof newAction.meta.analytics) {
    case 'boolean':
      newAction.meta.analytics = {
        eventType: defaultEventType,
        eventPayload: {
          properties: {},
        },
      };
      break;

    case 'string':
      newAction.meta.analytics = {
        eventType: action.meta.analytics,
        eventPayload: {
          properties: {},
        },
      };
      break;

    case 'object':
      trackMeta = newAction.meta.analytics;
      trackMeta.eventType = trackMeta.eventType || defaultEventType;
      trackMeta.eventPayload = trackMeta.eventPayload || {};
      trackMeta.eventPayload.properties = trackMeta.eventPayload.properties || {};
      break;

    default:
      newAction.meta.analytics = {
        eventType: defaultEventType,
        eventPayload: {
          properties: {},
        },
      };
      break;
  }

  return newAction;
};

/**
 * Expands the action's metadata to include an identify call where applicable
 *
 * @param {object} action - Action to expand with metadata
 * @param {object} store - The store where the app state lives
 * specififed in the action metadata
 * @returns {object} - The action augmented with analytics metadata
 */
const createIdentifyPayload = (action, store) => {
  const newAction = JSON.parse(JSON.stringify(action));
  const company = newAction.meta.company || null;
  const phone = newAction.meta.phone || null;
  const { id, email } = selectors.getIdentityState(store.getState());
  if (company && phone) {
    const trackEvent = newAction.meta.analytics;
    const identifyEvent = {
      eventType: EventTypes.identify,
      eventPayload: {
        userId: id,
        traits: {
          email,
          company,
          phone,
        },
      },
    };
    newAction.meta.analytics = [trackEvent, identifyEvent];
    delete newAction.meta.company;
    delete newAction.meta.phone;
  }
  return newAction;
};

/**
 * Gets the event name for a given action, either from the event map or directly
 * from the action itself
 *
 * @param {object} action - Action to get the event name for
 * @returns {string} - The event name for the action
 */
const getEventName = (action) => {
  if (eventMap[action.type]) {
    return eventMap[action.type].eventName;
  }
  if (action.meta.service) {
    return 'Instance Created - '.concat(action.meta.service, ' V5');
  }
  if (action.meta.trackClick) {
    if (action.meta.buttonName.includes(',')) {
      return action.meta.buttonName.replace(/,/g, '').concat(' Button Clicked');
    }
    return `${action.meta.buttonName} Button Clicked`;
  }
  return action.meta.analytics.eventPayload.event || action.type;
};

/**
 * Gets the event name for a given action, either from the event map or directly
 * from the action itself
 *
 * @param {object} action - Action to get the event name for
 * @returns {string} - The event name for the action
 */
const getCatagoryName = (action) => {
  if (eventMap[action.type]) {
    return eventMap[action.type].eventCatagory;
  }
  // if (action.meta.category) {
  //   return action.meta.category;
  // }
  return null;
};

/**
 * Gets the global tracking properties from the store
 *
 * @param {object} store - The store where the app state lives
 * @returns {object} - An object of global properties that should be included
 *  in each tracking event
 */
const getGlobalTrackingProps = (store) => {
  const { email, roles } = selectors.getIdentityState(store.getState());
  // TODO: Add more global props here when we know what they should be
  return {
    source: 'GUIV',
    email,
    roles,
  };
};

/**
 * Determines if an action already has analytics metadata
 *
 * @param {object} action - The action to examine
 * @returns {boolean} - Whether or not the action already contains analytics
 *  metadata
 */
const hasAnalyticsMeta = (action) => action && action.meta && action.meta.analytics;

/**
 * Determines if an action represents an page event that is auto-tracked by
 * the Segment library
 *
 * @param {object} action - The action to examine
 * @returns {boolean} - Whether or not the action is an autotracked page event
 */
const isAutoTrackedPageEvent = (action) => action && AUTOTRACKED_EVENTS.includes(action.type);

/**
 * Middleware function that augments the action with analytics metadata
 *
 * @param {object} action - The action to examine
 * @returns {function} - Middleware function that adds the metadata to the
 * current action and calls the next action.
 */
const trackingAugmentor = (store) => (next) => (action) => {
  let augAction = action;
  const isAutoTracked = isAutoTrackedPageEvent(action);
  if (hasAnalyticsMeta(action) || isAutoTracked) {
    augAction = expandAnalyticsMetadata(action, isAutoTracked ? EventTypes.page : EventTypes.track);
    augAction.meta.analytics.eventPayload.event = getEventName(augAction);
    if (augAction.meta.analytics.eventType === EventTypes.track) {
      augAction.meta.analytics.eventPayload.properties.action = getEventName(augAction);
      augAction.meta.analytics.eventPayload.properties.category = getCatagoryName(augAction);
    }
    Object.assign(augAction.meta.analytics.eventPayload.properties, getGlobalTrackingProps(store));
    augAction = createIdentifyPayload(augAction, store);
  }
  return next(augAction);
};

export default trackingAugmentor;
